/**
 * Billing exceptions (superadmin): helpers shared by the page and its tests.
 * API: /admin/billing-exceptions/* (aggregator docs/BILLING_EXCEPTIONS.md).
 */

export const BASE = '/admin/billing-exceptions';
export const FLOWS = ['activation', 'renewal', 'charge'];
export const STAGES = ['charge', 'activation', 'subscribe'];

export const FLOW_LABEL = {
  activation: 'Sign-up (activation)',
  renewal: 'Renewal due',
  charge: '9mobile charge call',
};

export const FLOW_HELP = {
  activation: 'MTN and Airtel accepted our subscribe request, but no notification for the number on that product has arrived since.',
  renewal: 'The paid validity ended more than the grace period ago and nothing (renewal, failed charge or opt-out) has arrived since the last charge.',
  charge: 'We called 9mobile’s charge API and got no result code back (timeout, HTTP error or unreadable answer).',
};

export const STAGE_LABEL = { charge: 'Charge', activation: 'Activation', subscribe: 'Subscribe (consent)' };

/** True only for superadmins (users.user_type SUPPER_ADMIN, the backend's spelling). */
export const isSuperAdmin = (user) => ['SUPPER_ADMIN', 'SUPER_ADMIN'].includes(String(user?.user_type || '').toUpperCase());

/** Drops nav items marked superadmin for everyone else. */
export const filterSuperadminNav = (nav, user) =>
  nav
    .map((g) => ({ ...g, items: g.items.filter((i) => !i.superadmin || isSuperAdmin(user)) }))
    .filter((g) => g.items.length > 0);

/** Query parameters for summary/list from the applied filters (blank values dropped by apiGet). */
export const queryParams = (f, extra = {}) => ({
  from: f.from,
  to: f.to,
  telco: f.telco,
  partner_id: f.partner_id,
  product_id: f.product_id,
  phone: f.phone,
  window_minutes: f.window_minutes,
  grace_hours: f.grace_hours,
  ...extra,
});

/** Export body for POST /exports. */
export const exportBody = (kind, f, extra = {}) => {
  const body = { kind, from: f.from, to: f.to };
  ['telco', 'phone', 'code', 'stage', 'message', 'flow'].forEach((k) => {
    const v = extra[k] !== undefined ? extra[k] : f[k];
    if (v) body[k] = v;
  });
  ['partner_id', 'product_id', 'window_minutes', 'grace_hours'].forEach((k) => {
    if (f[k]) body[k] = Number(f[k]);
  });
  return body;
};

const dayDiff = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000);

/** Exports cover at most 31 days. Returns an error message or null. */
export const exportRangeError = (f, maxDays = 31) => {
  if (!f.from || !f.to) return 'Choose a start and end date.';
  const span = dayDiff(f.from, f.to);
  if (span < 0) return 'Choose a start date on or before the end date.';
  if (span + 1 > maxDays) return `Exports cover at most ${maxDays} days. Narrow the date range.`;
  return null;
};

/** "08030000001" etc. accepted; returns true when the input looks like a Nigerian number (or is empty). */
export const phoneLooksValid = (s) => {
  const v = String(s || '').replace(/[\s()-]/g, '').replace(/^\+/, '');
  if (!v) return true;
  return /^(234\d{10}|0\d{10}|[789]\d{9})$/.test(v);
};

/** Page count text: "Rows 51–100 of 1,234". */
export const rangeText = (page, size, total, fmt = (n) => String(n)) => {
  if (!total) return 'No rows';
  const start = (page - 1) * size + 1;
  const end = Math.min(page * size, total);
  return `Rows ${fmt(start)}–${fmt(end)} of ${fmt(total)}`;
};

/** Age in hours as "3.5 h" / "2.1 d". */
export const ageText = (h) => {
  const n = Number(h);
  if (Number.isNaN(n)) return '—';
  if (n < 48) return `${n.toFixed(1)} h`;
  return `${(n / 24).toFixed(1)} d`;
};

/** Total of a flow from the missing summary (0 when absent or failed). */
export const flowCount = (summary, flow) => (summary?.flows || []).find((f) => f.flow === flow)?.count || 0;
